import type { TAEInfo } from '@/api/hcp/classification/hooks/useGetTAEInfoForHCP';
import type { ClinicalTrialsTotalCount } from '@/api/queries/hcp/scientificInformation/useGetHCPClinicalTrialsCount';
import type { PublicationsTotalCount } from '@/api/queries/hcp/scientificInformation/useGetPublications';
import type { Dayjs } from 'dayjs';
import type { HcpAssociation } from './association';
import type { Consents } from './consents';
import type { Donts } from './donts';
import type { HCO } from './hco';
import type { TaeScoring } from './taescoring';

export const BK_SEPERATOR = '^^';

export const buildHcpBk = (countryCode: string, uci: string): string => `${countryCode}${BK_SEPERATOR}${uci}`;
export const getCountryCode = (bk: string): string => {
  if (!bk) {
    throw new Error('getCountryCode: empty business key');
  } else if (!bk.includes(BK_SEPERATOR)) {
    throw new Error(`getCountryCode: invalid business key '${bk}'`);
  }
  return bk.split(BK_SEPERATOR)[0];
};
export const getUCI = (bk: string): string => bk.split(BK_SEPERATOR)[1];

export interface HCPBase {
  uci?: string;
  bk?: string;
  id?: string;
  person: Person;
  type: string;
  classification: string[];
  tae?: TAEInfo[];
  speciality?: string;
  country: string;
  countryCode?: string;
  contact: Contact;
  nationalId?: string;
  hcp_consent_profiling_business_relation_flag: string;
  title: string;
  hcp_consent_profiling_communication_flag: string;
  consent_disclosure: string;
  consent_do_not_call: string;
  hcp_mdm_id: string;
  primaryPointOfContact: string;
  primaryPointOfContactEmail: string;
  hcpRegistrationCompleteFlag: boolean;
  websiteRegistrationDate?: string;
}

export interface HCPExtended extends HCPBase {
  hcoLocations?: HCO[];
  associations?: HcpAssociation[];
  socialMedia?: SocialMedia;
  consents?: Consents;
  donts?: Donts;
  activitysores?: TaeScoring[];
  topicscores?: TaeScoring[];
  clinicaltrialsponsor?: TaeScoring[];
  pubmed?: {
    link?: string;
    totalResults?: number;
  };
  sponsoredTrials?: {
    link?: string;
    totalResults?: number;
  };
  crmLink?: string;
  hco_crm_link?: string;
  clinicalTrialsTotalCount?: ClinicalTrialsTotalCount;
  publicationsTotalCount?: PublicationsTotalCount;
}

export interface HCP extends HCPBase {
  primaryLocation?: HCO | null;
  latestInteraction?: Dayjs;
  tae?: TAEInfo[];
  accountPlanInProgressCount: number | null;
}

export enum Country {
  UK = 'United Kingdom',
  France = 'France',
  Germany = 'Germany',
  Finland = 'Finland',
  Australia = 'Austria',
}

export type Person = {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  age?: number;
  titles?: string[];
};

type SocialMedia = {
  twitter?: string;
  linkedIn?: string;
};

type Contact = {
  emails: string[];
  phoneNumbers: string[];
  linkedIn?: string;
};

export type HcpSearchObject = {
  firstName: string;
  lastName: string;
  uci: string;
  email: string;
  hco: string;
  nationalId: string;
  specialty: string[];
  country: string[];
};

export type HcpCTAndPubmedCount = {
  clinicalTrials: number;
  pubmed: number;
};
